// src/AnnouncementManagement.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';
import FileUpload from './FileUpload'; // <-- (เพิ่ม 1) Import Component

const API_URL = 'https://rys-backend.onrender.com/api';

function AnnouncementManagement() {
  const [announcements, setAnnouncements] = useState([]);
  const [loadingAnns, setLoadingAnns] = useState(true);
  
  // --- States สำหรับฟอร์ม ---
  const [annTitle, setAnnTitle] = useState('');
  const [annImageUrl, setAnnImageUrl] = useState('');
  const [annLinkUrl, setAnnLinkUrl] = useState('');
  
  // (เพิ่ม 1) State สำหรับโหมดแก้ไข
  const [editingAnnId, setEditingAnnId] = useState(null); // (ถ้ามี ID = โหมดแก้ไข)

  // (เพิ่ม 2) States สำหรับ Modal ลบ
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingAnn, setDeletingAnn] = useState(null); // (เก็บ {id, title})

  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();

  const fetchAnnouncements = async () => {
    setLoadingAnns(true);
    try {
      const response = await axios.get(`${API_URL}/announcements`); 
      setAnnouncements(response.data);
    } catch (err) {
      console.error("Error fetching announcements:", err);
      toast.error("ไม่สามารถโหลดประกาศได้");
    } finally {
      setLoadingAnns(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, [getAuthHeaders]);

  // (ฟังก์ชันใหม่) สำหรับเคลียร์ฟอร์ม
  const clearForm = () => {
    setEditingAnnId(null);
    setAnnTitle('');
    setAnnImageUrl('');
    setAnnLinkUrl('');
  };

  // (อัปเกรด 3) เปลี่ยนชื่อเป็น handleSubmit (รองรับทั้ง Create และ Update)
  const handleSubmitAnnouncement = async (e) => {
    e.preventDefault();
    if (!annTitle || !annImageUrl) {
      toast.error("กรุณากรอก 'ชื่อเรื่อง' และ 'อัปโหลดรูปภาพ'");
      return;
    }
    
    const payload = { 
      title: annTitle, 
      image_url: annImageUrl, 
      link_url: annLinkUrl || '#' 
    };

    try {
      if (editingAnnId) {
        // --- โหมดแก้ไข (UPDATE) ---
        const response = await axios.put(`${API_URL}/announcements/${editingAnnId}`, payload, getAuthHeaders());
        toast.success(response.data.msg);
      } else {
        // --- โหมดสร้างใหม่ (CREATE) ---
        const response = await axios.post(`${API_URL}/announcements`, payload, getAuthHeaders());
        toast.success(response.data.msg);
      }
      clearForm(); // เคลียร์ฟอร์ม
      fetchAnnouncements(); // โหลดประกาศใหม่
    } catch (err) {
      console.error("Submit Error:", err);
      toast.error(err.response?.data?.msg || 'ดำเนินการไม่สำเร็จ');
    }
  };
  
  // (เพิ่ม 4) ฟังก์ชันเมื่อคลิก "แก้ไข"
  const handleEditClick = (ann) => {
    setEditingAnnId(ann.id);
    setAnnTitle(ann.title);
    setAnnImageUrl(ann.image_url);
    setAnnLinkUrl(ann.link_url || '');
    // เลื่อนจอขึ้นไปบนสุดเพื่อให้ Admin เห็นฟอร์ม
    window.scrollTo(0, 0); 
  };
  
  // (อัปเกรด 5) ฟังก์ชัน "เปิด" Modal ลบ (ลบ window.confirm)
  const handleOpenDeleteModal = (ann) => {
    setDeletingAnn(ann);
    setIsDeleteModalOpen(true);
  };
  
  // (เพิ่ม 6) ฟังก์ชัน "ยืนยัน" การลบ (ใน Modal)
  const handleConfirmDelete = async () => {
    if (!deletingAnn) return;
    try {
      const response = await axios.delete(`${API_URL}/announcements/${deletingAnn.id}`, getAuthHeaders());
      toast.success(response.data.msg);
      fetchAnnouncements();
    } catch (err) {
      toast.error(err.response?.data?.msg || 'ลบประกาศไม่สำเร็จ');
    } finally {
      setIsDeleteModalOpen(false);
      setDeletingAnn(null);
    }
  };

  if (user?.role !== 'admin') {
    navigate('/');
    return null;
  }

  return (
    <>
      <div className="container mx-auto p-4 sm:p-6 lg:p-8">
        <button
          onClick={() => navigate('/admin')}
          className="mb-6 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition duration-300"
        >
          &larr; กลับไปหน้า Admin Panel
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* ฟอร์มสร้าง/แก้ไข ประกาศ */}
          <div className="lg:col-span-1">
            <div className="w-full bg-white rounded-lg shadow-xl p-8 sticky top-28">
              <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">
                {/* (อัปเกรด) เปลี่ยนหัวข้อตามโหมด */}
                {editingAnnId ? 'แก้ไขประกาศ' : 'เพิ่มประกาศใหม่'}
              </h2>
              
              <form onSubmit={handleSubmitAnnouncement} className="space-y-6">
                <div>
                  {/* (อัปเกรด) เพิ่ม * (ดอกจัน) */}
                  <label htmlFor="annTitle" className="block text-base font-medium text-gray-700">ชื่อเรื่อง (Title) *</label>
                  <input
                    type="text" id="annTitle" value={annTitle}
                    onChange={(e) => setAnnTitle(e.target.value)}
                    required
                    className="mt-1 block w-full px-4 py-3 text-lg border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500"
                    placeholder="เช่น 'ประกาศวันหยุดบริษัท'"
                  />
                </div>
                <div>
                  <label className="block text-base font-medium text-gray-700">Image * (ลากไฟล์มาวาง)</label>
                  {/* (แทนที่ Input Text ด้วย FileUpload) */}
                  <FileUpload 
                    // (ฟังก์ชันที่จะถูกเรียกเมื่ออัปโหลดสำเร็จ)
                    onUploadSuccess={(fileUrl) => setAnnImageUrl(fileUrl)}
                    // (ส่ง URL รูปเก่าไปให้ (ถ้ามี) เพื่อแสดง Preview)
                    currentImageUrl={annImageUrl} 
                    getAuthHeaders={getAuthHeaders}
                  />
                </div>
                {/* ------------------- */}
                
                <div>
                  <label htmlFor="annLinkUrl" className="block text-base font-medium text-gray-700">Link URL (ลิงก์ปลายทาง)</label>
                  <input
                    type="text" id="annLinkUrl" value={annLinkUrl}
                    onChange={(e) => setAnnLinkUrl(e.target.value)}
                    className="mt-1 block w-full px-4 py-3 text-lg border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500"
                    placeholder="https://... (ถ้ามี)"
                  />
                </div>
                <div>
                  <button
                    type="submit"
                    className={`w-full px-4 py-3 font-semibold text-xl text-white rounded-md transition duration-300 ${
                      editingAnnId 
                      ? 'bg-blue-600 hover:bg-blue-700' 
                      : 'bg-green-600 hover:bg-green-700'
                    }`}
                  >
                    {/* (อัปเกรด) เปลี่ยนปุ่มตามโหมด */}
                    {editingAnnId ? 'บันทึกการแก้ไข' : 'เพิ่มประกาศ'}
                  </button>
                  
                  {/* (เพิ่ม) ปุ่มยกเลิกการแก้ไข */}
                  {editingAnnId && (
                    <button
                      type="button"
                      onClick={clearForm}
                      className="w-full mt-3 px-4 py-2 font-semibold text-base text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition duration-300"
                    >
                      ยกเลิกการแก้ไข
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
          
          {/* ตาราง/ลิสต์ ประกาศ */}
          <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow-xl">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 border-b pb-3">
              ประกาศที่มีอยู่
            </h2>
            <div className="overflow-x-auto">
              {loadingAnns ? <p>Loading announcements...</p> : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-base font-medium text-gray-500 uppercase">หัวข้อ</th>
                      <th className="px-6 py-3 text-left text-base font-medium text-gray-500 uppercase">ที่อยู่รูปภาพประกาศ</th>
                      <th className="px-6 py-3 text-right text-base font-medium text-gray-500 uppercase">ดำเนินการ</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {announcements.map(ann => (
                      <tr key={ann.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-lg text-gray-800">{ann.title}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-lg text-gray-600 truncate max-w-xs">{ann.image_url}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-lg font-medium space-x-4">
                          {/* (เพิ่ม) ปุ่มแก้ไข */}
                          <button
                            onClick={() => handleEditClick(ann)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Edit Announcement"
                          >
                            แก้ไข
                          </button>
                          <button
                            onClick={() => handleOpenDeleteModal(ann)} // (อัปเกรด)
                            className="text-red-600 hover:text-red-900"
                            title="Delete Announcement"
                          >
                            ลบ
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* --- (เพิ่ม 7) Modal สำหรับยืนยันการลบ --- */}
      {isDeleteModalOpen && deletingAnn && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white w-full max-w-lg p-6 rounded-lg shadow-xl m-4">
            <h2 className="text-2xl font-semibold mb-5 text-gray-800 border-b pb-3">
              ยืนยันการลบ
            </h2>
            <p className="mb-6 text-lg">
              คุณแน่ใจหรือไม่ว่าต้องการลบประกาศ: <br/>
              <strong className="text-red-600">{deletingAnn.title}</strong>?
            </p>
            <div className="flex justify-end space-x-3 pt-6">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-5 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition duration-300"
              >
                ยกเลิก
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="px-5 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition duration-300"
              >
                ยืนยันการลบ
              </button>
            </div>
          </div>
        </div>
      )}
    </> 
  );
}

export default AnnouncementManagement;